import { defineStore } from "pinia";
import { CONFIG_ENDPOINT } from "@/constants/Endpoints";
import {
  ENV_CONFIG_PROPERTY,
  ENV_CONFIG_PROPERTIES,
  CONFIG_SORT_BY,
  getEnvironmentFromCode,
} from "@/constants/EnvConfigProperties";
import { LOGGER } from "@/util/logger";
import { SORT_ORDER, TYPES } from "@/constants/CommonWebConstants";
import ApiWorkerManager from "@/ApiWorkerManager";

export const useConfigStore = defineStore("config", {
  state: () => ({
    apiWorkerManager: new ApiWorkerManager(),
    references: new Map(),
    districts: [],
    counties: [],
    municipalities: [],
    envConfigProperties: [],
    appBuildInfo: {},
    departmentStructureTypeDescriptions: [],
    elementDefinitions: [],
    elementChildren: {},
    elementStateDefinitions: {},
    users: [],
    scourCriticalBridgeIndicators: [],
    docTypes: [],
    flexActions: [],
    bridgeActions: [],
    codeFilters: [],
    organizations: [],
    spnOrganizations: [],
    nbisBridgeInspectors: [],
    nbisNSTMBridgeInspectors: [],
    nbisTunnelInspectors: [],
    inspectors: [],
    underwaterDivers: [],
    inspectionReportSection: {},
    inspectorIds: [],
    commentsFilters: [],
    serviceTypes: [],
  }),
  getters: {
    getAppBuildInfo() {
      return this.appBuildInfo;
    },
    isEnvironment: (state) => (env) => {
      return Boolean(state.appBuildInfo.environment?.code === env.code);
    },
    addUnitSuffix: () => (value, addUomSuffix, unit) => {
      if (addUomSuffix) {
        return value + " " + unit;
      } else {
        return value;
      }
    },
    getUsers: (state) => {
      return state.users;
    },
    getOrganizations: (state) => {
      return state.organizations;
    },
    getSPNOrganizations: (state) => {
      return state.spnOrganizations;
    },
    getFlexActions: (state) => (serviceTypeOn) => {
      const filter = state.codeFilters.find((a) => a.filterCriterion.includes(serviceTypeOn))?.filterCode.split(",");
      let flexActionOptions = [];
      for (const action of state.flexActions ?? []) {
        if (filter.some((a) => Number.parseInt(a) == Number.parseInt(action.flexAction.fxActKey))) {
          flexActionOptions.push({
            title: action.flexAction.fxActKey + " - " + action.flexAction.fxName,
            value: action.flexAction.fxActKey,
          });
        }
      }
      flexActionOptions.sort(function (a, b) {
        return Number.parseInt(a.value) - Number.parseInt(b.value);
      });
      return flexActionOptions;
    },
    getFlexAction: (state) => (key) => {
      let action = state.flexActions.find((c) => c.flexAction.fxActKey === key);
      return action?.flexAction;
    },
    getFlexActionTitle: (state) => (key) => {
      if (key == "") {
        return key;
      } else {
        let action = state.flexActions.find((c) => c.flexAction.fxActKey === key);
        return action?.flexAction?.fxActKey + " - " + action?.flexAction?.fxName;
      }
    },
    getBridgeActions: (state) => {
      return state.bridgeActions
        .filter((a) => {
          const idValue = Number.parseInt(a.bridgeAction.actionTypeKey);
          //BUG# 84024
          return idValue < 31 || idValue > 43 || idValue == 40;
        })
        .map((action) => {
          return {
            title: action.bridgeAction.actionTypeKey + " - " + action.bridgeAction.aTypeShort,
            value: action.bridgeAction.actionTypeKey,
          };
        })
        .sort(function (a, b) {
          return Number.parseInt(a.value) - Number.parseInt(b.value);
        });
    },
    getBridgeAction: (state) => (key) => {
      let action = state.bridgeActions.find((c) => c.bridgeAction.actionTypeKey === key);
      return action?.bridgeAction;
    },
    getBridgeActionTitle: (state) => (key) => {
      if (key == "") {
        return key;
      } else {
        let action = state.bridgeActions.find((c) => c.bridgeAction.actionTypeKey === key);
        return action?.bridgeAction?.actionTypeKey + " - " + action?.bridgeAction?.aTypeShort;
      }
    },
    getUser: (state) => (userKey) => {
      return state.users.find((u) => u.userId == userKey);
    },
    getUserDisplayNameByUserId: (state) => (userId) => {
      if (!userId) {
        return "";
      }
      const user = state.users.find((u) => u.userId == userId);
      if (!user) {
        return userId;
      }
      return `${user.userFirstName} ${user.userLastName} (${user.userId.toString()})`;
    },
    getUserNameByUserLogin: (state) => (userLogin) => {
      const user = state.users.find((u) => u.userLogin == userLogin);
      return user ? `${user.userFirstName} ${user.userLastName}` : userLogin;
    },
    getNbisBridgeInspectors: (state) => {
      return state.nbisBridgeInspectors;
    },
    getNbisNSTMBridgeInspectors: (state) => {
      return state.nbisNSTMBridgeInspectors;
    },
    getNbisTunnelInspectors: (state) => {
      return state.nbisTunnelInspectors;
    },
    getInspectors: (state) => {
      return state.inspectors;
    },
    getUnderwaterDivers: (state) => {
      return state.buildTeamLeadersAndUnderwaterDivers(state.underwaterDivers).sort(state.compare);
    },
    getInspectionReportSection: (state) => {
      return state.inspectionReportSection;
    },
    getInspectorId: (state) => (userId) => {
      if (!userId) {
        return "";
      }
      return state.inspectorIds.find((i) => i.userId.toString() === userId.toString())?.inspectorId || "";
    },
    switchNull: () => (value) => {
      if (value) {
        return String(value);
      } else {
        return " ";
      }
    },
    compare: () => (a, b) => {
      const aLastName = (a.title?.split(".")[1]?.split("(")[0] || "").toLowerCase();
      const bLastName = (b.title?.split(".")[1]?.split("(")[0] || "").toLowerCase();

      if (aLastName < bLastName) {
        return -1;
      } else if (aLastName > bLastName) {
        return 1;
      } else {
        const aFirstName = (a.title?.split(".")[0] || "").toLowerCase();
        const bFirstName = (b.title?.split(".")[0] || "").toLowerCase();

        if (aFirstName < bFirstName) {
          return -1;
        } else if (aFirstName > bFirstName) {
          return 1;
        } else {
          return 0;
        }
      }
    },
    buildTeamLeadersAndUnderwaterDivers: () => (users) => {
      return users?.map((user) => {
        return {
          value: user.userId.toString(),
          title: `${user.userFirstName?.substring(0, 1)}. ${user.userLastName} (${user.userId.toString()})`,
        };
      });
    },
    getTeamLeaders: (state) => (structure, nstmChecked) => {
      let teamLeaders = [];
      let reportGroup = structure?.REPORTGROUP ? structure.REPORTGROUP : structure?.structureBridge?.reportGroup;
      let defaultUserKey = structure?.INSPUSRKEY ? structure.INSPUSRKEY : structure?.inspection?.inspectionUserId;
      switch (reportGroup?.trim()) {
        case "S1":
        case "L1":
        case "A1":
          teamLeaders = nstmChecked
            ? state.buildTeamLeadersAndUnderwaterDivers(state.getNbisNSTMBridgeInspectors)
            : state.buildTeamLeadersAndUnderwaterDivers(state.getNbisBridgeInspectors);
          break;
        case "T1":
          teamLeaders = state.buildTeamLeadersAndUnderwaterDivers(state.getNbisTunnelInspectors);
          break;
        default:
          teamLeaders = state.buildTeamLeadersAndUnderwaterDivers(state.getInspectors);
          break;
      }
      if (!teamLeaders.some((l) => l.value == defaultUserKey)) {
        const currentLeaderToAdd = state.getUser(defaultUserKey);
        if (currentLeaderToAdd) {
          teamLeaders.push({
            value: currentLeaderToAdd.userId.toString(),
            title: `${currentLeaderToAdd.userFirstName?.substring(0, 1)}. ${
              currentLeaderToAdd.userLastName
            } (${currentLeaderToAdd.userId.toString()})`,
          });
        } else {
          teamLeaders.push({
            title: defaultUserKey,
            value: defaultUserKey,
          });
        }
      }
      return teamLeaders.sort(state.compare);
    },
    getScourCriticalBridgeIndicatorReturnObject: (state) => (structure, underwaterInspection, structureUnit) => {
      let strucConfig = structure.deptMainStrucConfig;

      if (strucConfig?.charAt(0) == "0") {
        strucConfig = strucConfig?.charAt(1);
      }
      return state.scourCriticalBridgeIndicators.find(
        (scbis) =>
          scbis.strUnitType == structureUnit.legacyStructureType &&
          (scbis.foundType == state.switchNull(underwaterInspection.foundType) || scbis.foundType == "ANY") &&
          (scbis.subUnitType == state.switchNull(underwaterInspection.subUnitType) || scbis.subUnitType == "ANY") &&
          (scbis.scourHole == state.switchNull(underwaterInspection.scourHole) || scbis.scourHole == "ANY") &&
          (scbis.changeSinceLastInsp == state.switchNull(underwaterInspection.changeSinceLastInsp) ||
            scbis.changeSinceLastInsp == "ANY") &&
          (scbis.streamBedMat == state.switchNull(underwaterInspection.streamBedMaterial) ||
            scbis.streamBedMat == "ANY") &&
          (scbis.moveMnt == state.switchNull(underwaterInspection.movement) || scbis.moveMnt == "ANY") &&
          (scbis.deptMainStrConf == state.switchNull(strucConfig) || scbis.deptMainStrConf == "ANY"),
      );
    },
    cleanupScourCriticalBridgeIndicator: () => (structure, scbiCodes) => {
      if (scbiCodes.includes(6)) {
        structure.overallOSA = "6";
      } else if (scbiCodes.includes(1) || scbiCodes.includes(2) || scbiCodes.includes(3) || scbiCodes.includes(4)) {
        structure.overallOSA = Math.min(...scbiCodes).toString();
      } else if (scbiCodes.includes(7)) {
        structure.overallOSA = "7";
      } else if (scbiCodes.includes(5)) {
        structure.overallOSA = "5";
      } else if (scbiCodes.includes(8)) {
        structure.overallOSA = "8";
      }
      if (structure.scbiSource == "O") {
        structure.scourCritical = structure.overallOSA;
      }
    },
    filterWingwalls: () => (structureUnits, inspectionsUnderwater) => {
      const wingWallKeys = structureUnits?.filter((ww) => ww.legacyStructureType == "W").map((su) => su.strUnitKey);

      return inspectionsUnderwater?.filter((i) => !wingWallKeys.includes(i.strUnitKey));
    },
    calculateScourVulnerability: (state) => (structure) => {
      const { inspectionsUnderwater, structureUnits } = structure;

      const filteredUWInsp = state.filterWingwalls(structureUnits, inspectionsUnderwater) ?? [];

      if (isServiceTypeUnder01234(structure.servTypUnd)) {
        structure.scourVulnerability = "N";
      } else if (isPorX(filteredUWInsp)) {
        structure.scourVulnerability = "U";
      } else {
        const scbiCodes = filteredUWInsp.map(({ scbiCode }) => Number.parseInt(scbiCode));

        if (scbiCodes.includes(6)) {
          structure.scourVulnerability = "0";
        } else if (scbiCodes.some((code) => [3, 2, 1, 0].includes(code))) {
          structure.scourVulnerability = "D";
        } else if (scbiCodes.includes(7)) {
          structure.scourVulnerability = "C";
        } else if (
          filteredUWInsp.some(
            ({ scbiCode, streamBedMaterial }) =>
              [5, 8].includes(Number.parseInt(scbiCode)) && streamBedMaterial === "D9",
          )
        ) {
          structure.scourVulnerability = "B";
        } else if (
          filteredUWInsp.some(
            ({ scbiCode, scourHole, streamBedMaterial }) =>
              Number.parseInt(scbiCode) === 4 && Number.parseInt(scourHole) <= 5 && streamBedMaterial !== "D9",
          )
        ) {
          structure.scourVulnerability = "D";
        } else if (
          filteredUWInsp.some(
            ({ scbiCode, streamBedMaterial }) =>
              [5, 8].includes(Number.parseInt(scbiCode)) && streamBedMaterial !== "D9",
          )
        ) {
          structure.scourVulnerability = "A";
        } else if (
          filteredUWInsp.some(
            ({ scbiCode, scourHole, streamBedMaterial }) =>
              Number.parseInt(scbiCode) === 4 && Number.parseInt(scourHole) > 5 && streamBedMaterial !== "D9",
          )
        ) {
          structure.scourVulnerability = "A";
        } else if (scbiCodes.includes(9)) {
          structure.scourVulnerability = "A";
        } else {
          structure.scourVulnerability = "0";
        }
      }
    },
    getScourCriticalBridgeIndicator: (state) => (structure) => {
      const { structureUnits, inspectionsUnderwater } = structure;
      const scbiCodes = [];
      const filteredUWInsp = state.filterWingwalls(structureUnits, inspectionsUnderwater);

      for (const underwaterInspection of filteredUWInsp ?? []) {
        const structureUnit = structureUnits?.find((su) => su.strUnitKey == underwaterInspection.strUnitKey);
        const returnObject = state.getScourCriticalBridgeIndicatorReturnObject(
          structure,
          underwaterInspection,
          structureUnit,
        );
        underwaterInspection.scbiCode = returnObject ? returnObject.scbiCode : "6";
        underwaterInspection.scbiCase = returnObject ? returnObject.scbiCase : "4";
        scbiCodes.push(Number.parseInt(underwaterInspection.scbiCode));
      }
      state.cleanupScourCriticalBridgeIndicator(structure, scbiCodes);
      state.calculateScourVulnerability(structure);
    },
    getCountyByFips: (state) => (value) => {
      return state.counties.find((r) => r.fipsCode === value);
    },
    getCountyByCountyCode: (state) => (value) => {
      return state.counties.find((r) => r.countyCode === value);
    },
    getDistrictByDistrictNum: (state) => (value) => {
      return state.districts.find((r) => r.districtNum === value);
    },
    getMunicipalityByPlaceCode: (state) => (value) => {
      return state.municipalities.find((r) => r.municipalityCode === value);
    },
    getConfigProperty: (state) => (propertyName) => {
      let property = state.properties.find((element) => {
        return element.name === propertyName;
      });
      return property ? property.value : null;
    },
    getCountiesByDistrict: (state) => (districts) => {
      const countyArr = [];
      let filteredCounties = state.counties.filter((c) => {
        return districts?.includes(c.districtNum);
      });
      if (filteredCounties.length === 0) filteredCounties = state.counties;
      for (const c of filteredCounties ?? []) {
        countyArr.push({
          value: c.countyCode,
          title: `${c.countyCode} - ${c.countyName}`,
        });
      }
      return countyArr;
    },
    getCountyFipsByDistrict: (state) => (districts) => {
      const countyArr = [];
      let filteredCounties = state.counties.filter((c) => {
        return districts?.includes(c.districtNum);
      });
      if (filteredCounties.length === 0) filteredCounties = state.counties;
      for (const c of filteredCounties ?? []) {
        countyArr.push({
          value: c.fipsCode,
          title: `${c.countyCode} - ${c.countyName}`,
        });
      }
      return countyArr;
    },
    getMunicipalitiesByCounty: (state) => (counties) => {
      let municipalityArr = [];
      let filteredMunis = state.municipalities.filter((m) => {
        return counties?.includes(m.countyCode);
      });
      if (filteredMunis.length === 0) filteredMunis = state.municipalities;
      filteredMunis.sort(function (a, b) {
        return a.countyCode - b.countyCode || a.municipalityNum - b.municipalityNum;
      });
      for (const m of filteredMunis ?? []) {
        municipalityArr.push({
          value: m.value,
          title: m.title,
        });
      }
      return municipalityArr;
    },
    getEnvConfigValue: (state) => (property) => {
      let envConfigProperty = state.envConfigProperties.find((element) => {
        return element.property === property;
      });
      return envConfigProperty ? envConfigProperty.value : null;
    },
    // Default to include `Code - Description` in title.
    getReferenceValues: (state) => (referenceName, sortBy, sortOrder, removeCodeInDesc, valueType, showLongDesc) => {
      let values = [];
      values = removeCodeInDescription(state, removeCodeInDesc, referenceName, showLongDesc);
      if (sortBy == CONFIG_SORT_BY.VALUE) {
        values = sortByDescription(sortOrder, values, valueType);
      } else if (sortOrder == SORT_ORDER.DESC) {
        values?.sort((a, b) => b.title?.localeCompare(a.title));
      } else {
        values?.sort((a, b) => a.title?.localeCompare(b.title));
      }

      return values;
    },
    getDepartmentStructureTypeDescriptions: (state) => {
      return state.departmentStructureTypeDescriptions;
    },
    getDepartmentStructureTypeDescription: (state) => (value) => {
      try {
        return state.departmentStructureTypeDescriptions.find((r) => r.deptStructureTypeId === value)
          ?.deptStructureDesc;
      } catch (e) {
        let error = Object.assign(e, {
          method: "getDepartmentStructureTypeDescription",
          value,
        });
        LOGGER.logException(error);
        return "";
      }
    },
    getReferenceValue: (state) => (referenceName, value, isDescriptionOnly, showLongDesc) => {
      const refTable = state.references.get(referenceName);
      try {
        if (value.length !== 0) {
          value = value?.trim();
          const reference = refTable.find((r) => r.value == value);
          const longValue = reference.title.long
            ? `${reference.value} - ${reference.title.long}`
            : `${reference.value}`;
          const shortValue = reference.title.short
            ? `${reference.value} - ${reference.title.short}`
            : `${reference.value}`;
          if (showLongDesc) {
            return isDescriptionOnly ? reference.title.long : longValue;
          } else {
            return isDescriptionOnly ? reference.title.short : shortValue;
          }
        }
      } catch (e) {
        let error = Object.assign(e, {
          method: "getReferenceValue",
          referenceName,
          value,
        });
        LOGGER.logException(error);
      }
      return value;
    },
    getElementDefinition: (state) => (elementKey) => {
      try {
        return state.elementDefinitions.find((e) => e.elemKey == elementKey);
      } catch (e) {
        let error = Object.assign(e, {
          method: "getElementDefinition",
          elementKey,
        });
        LOGGER.logException(error);
        return elementKey;
      }
    },
    getAddElementDefinitions: (state) => (structureType) => {
      try {
        return state.elementDefinitions.filter(
          (e) =>
            e.elemSmartFlag != "Y" &&
            e.elemProtectSys != "Y" &&
            e.elemSubComp != "Y" &&
            e.structureType === structureType,
        );
      } catch (e) {
        let error = Object.assign(e, {
          method: "getAddElementDefinitions",
          structureType,
        });
        LOGGER.logException(error);
        return [];
      }
    },
    getElementStateDefinitions() {
      return (elemKey) => {
        try {
          let definitions = [];
          for (const d of this.elementStateDefinitions[elemKey] ?? []) {
            definitions.push({
              elemKey: elemKey,
              elemStateKey: d.elemStateKey,
              elemStateNameShort: d.elemStateNameShort,
              elemStateDesc: d.elemStateDesc,
            });
          }
          return definitions;
        } catch (e) {
          let error = Object.assign(e, {
            method: "getElementStateDefinitions",
            elemKey,
          });
          LOGGER.logException(error);
          return [];
        }
      };
    },
    getElementChildren() {
      return (elemKey) => {
        let elementWithChildren = {
          elemKey: elemKey,
          defects: [],
          protectiveSystems: [],
          subComponents: [],
        };
        try {
          for (const elem of this.elementChildren[elemKey] ?? []) {
            let elementDefinition = this.getElementDefinition(elem.elemChildKey);
            if (elementDefinition?.elemProtectSys === "Y") {
              //element protective systems
              let protectiveSystem = { SUB_COMPS: [] };
              protectiveSystem.elemKey = elem.elemChildKey;
              protectiveSystem.elemShortName = elementDefinition?.elemShortName;
              protectiveSystem.elemParentKey = elemKey;
              protectiveSystem.legalTextCD = elementDefinition?.legalTextCD;
              for (const grandChildKey of elem.elemGrandChildren ?? []) {
                const elemDef = this.getElementDefinition(grandChildKey);
                protectiveSystem.SUB_COMPS.push({
                  elemKey: grandChildKey,
                  elemShortName: elemDef?.elemShortName,
                  elemParentKey: elem.elemChildKey,
                  elemGrandparentKey: elemKey,
                  elementStateDefinitions: this.getElementStateDefinitions(grandChildKey),
                  legalTextCD: elemDef?.legalTextCD,
                });
              }
              elementWithChildren.protectiveSystems.push(protectiveSystem);
            } else if (elementDefinition?.elemSmartFlag === "Y") {
              //element defects
              let defect = {};
              defect.elemKey = elem.elemChildKey;
              defect.elemShortName = elementDefinition?.elemShortName;
              defect.elemParentKey = elemKey;
              defect.legalTextCD = elementDefinition?.legalTextCD;
              defect.elementStateDefinitions = this.getElementStateDefinitions(defect.elemKey);
              elementWithChildren.defects.push(defect);
            } else if (elementDefinition?.elemSubComp === "Y") {
              //element Sub components
              let subComponent = {};
              subComponent.elemKey = elem.elemChildKey;
              subComponent.elemShortName = elementDefinition?.elemShortName;
              subComponent.elemParentKey = elemKey;
              subComponent.legalTextCD = elementDefinition?.legalTextCD;
              subComponent.elementStateDefinitions = this.getElementStateDefinitions(subComponent.elemKey);
              elementWithChildren.subComponents.push(subComponent);
            }
          }
          return elementWithChildren;
        } catch (e) {
          let error = Object.assign(e, {
            method: "getElementChildren",
            elemKey,
          });
          LOGGER.logException(error);
          return {};
        }
      };
    },
    getDefaultDownloadDocKeys: (state) => (docCategories) => {
      try {
        return state.docTypes
          .filter((d) => docCategories.includes(d.docCategory) && d.iFormsDownloadInitial === "1")
          .map((t) => t.docTypeId);
      } catch (e) {
        let error = Object.assign(e, {
          method: "getDocKeys",
          docCategories,
        });
        LOGGER.logException(error);
        return docCategories;
      }
    },
    getDocType: (state) => (docTypeId) => {
      try {
        return state.docTypes.find((d) => d.docTypeId == docTypeId);
      } catch (e) {
        let error = Object.assign(e, {
          method: "getDocType",
          docTypeId,
        });
        LOGGER.logException(error);
        return docTypeId;
      }
    },
    getDocTypeIdBydocumentType: (state) => (documentType) => {
      const docType = state.docTypes.find((d) => d.documentType == documentType);
      return docType?.docTypeId ?? documentType;
    },
    getDocTypeReferenceValues: (state) => () => {
      try {
        return state.docTypes
          ?.map((d) => ({
            value: d.docTypeId,
            title: `${d.documentType} - ${d.docClass}`,
          }))
          .sort((a, b) => a.title.localeCompare(b.title));
      } catch (e) {
        let error = Object.assign(e, {
          method: "getDocTypeReferenceValues",
        });
        LOGGER.logException(error);
        return [];
      }
    },
    getUserId: (state) => (userName) => {
      const userId = userName
        ? state.users?.find((u) => u.userLogin?.trim().toLowerCase() === userName.toLowerCase())?.userId
        : null;

      return userId;
    },
    getCommentsFiltersByType: (state) =>
      new Map(state.commentsFilters.map((x) => [x.commentTypeCode, x.structureType])),
  },
  actions: {
    loadAppBuildInfo() {
      this.appBuildInfo = {
        // eslint-disable-next-line no-undef
        buildVersion: __APP_VERSION__,
        // eslint-disable-next-line no-undef
        buildTime: __BUILD_TIME__,
        environment: getEnvironmentFromCode(this.getEnvConfigValue(ENV_CONFIG_PROPERTY.ENVIRONMENT)),
      };
    },
    loadEnvProperties(user) {
      return new Promise((resolve) => {
        const data = {
          endPoint: CONFIG_ENDPOINT.ENV_CONFIG_VALUES,
          auth: user,
          urlParams: { properties: ENV_CONFIG_PROPERTIES.join(",") },
          method: "loadEnvProperties",
        };

        this.apiWorkerManager.postMessage(data).then((response) => {
          this.envConfigProperties = response.data;
          resolve();
        });
      });
    },
    loadDistricts(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.DISTRICTS,
        auth: user,
        method: "loadDistricts",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const d of response.data ?? []) {
          this.districts.push(d);
        }
      });
    },
    loadUsers(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.USERS,
        auth: user,
        method: "loadUsers",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.users = response.data;
      });
    },
    loadNbisBridgeInspectors(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.NBIS_BRIDGE_INSPECTORS,
        auth: user,
        method: "loadNbisBridgeInspectors",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.nbisBridgeInspectors = response.data;
      });
    },
    loadNbisNSTMBridgeInspectors(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.NBIS_NSTM_BRIDGE_INSPECTORS,
        auth: user,
        method: "loadNbisNSTMBridgeInspectors",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.nbisNSTMBridgeInspectors = response.data;
      });
    },
    loadNbisTunnelInspectors(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.NBIS_TUNNEL_INSPECTORS,
        auth: user,
        method: "loadNbisTunnelInspectors",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.nbisTunnelInspectors = response.data;
      });
    },
    loadInspectors(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.INSPECTORS,
        auth: user,
        method: "loadInspectors",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.inspectors = response.data;
      });
    },
    loadUnderwaterDivers(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.UNDERWATER_DIVERS,
        auth: user,
        method: "loadUnderwaterDivers",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.underwaterDivers = response.data;
      });
    },
    loadReferenceData(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.REFERENCES,
        auth: user,
        method: "loadReferenceData",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const ref of response.data ?? []) {
          let pairs = [];
          for (const [value, text] of Object.entries(ref.pair) ?? []) {
            pairs.push({ value, title: text });
          }
          this.references.set(ref.table, pairs);
        }
      });
    },
    loadCounties(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.COUNTIES,
        auth: user,
        method: "loadCounties",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const c of response.data ?? []) {
          this.counties.push(c);
        }
      });
    },
    loadMunicipalities(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.MUNICIPALITIES,
        auth: user,
        method: "loadMunicipalities",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const c of response.data ?? []) {
          this.municipalities.push(c);
        }
        this.municipalities.sort(
          (a, b) =>
            Number.parseInt(a.countyCode) - Number.parseInt(b.countyCode) ||
            Number.parseInt(a.municipalityNum) - Number.parseInt(b.municipalityNum),
        );
      });
    },
    loadCodeFilters(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.CODE_FILTERS,
        auth: user,
        method: "loadCodeFilters",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const c of response.data ?? []) {
          this.codeFilters.push(c);
        }
      });
    },
    loadScourCriticalBridgeIndicators(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.SCOUR_CRITICAL_BRIDGE_INDICATORS,
        auth: user,
        method: "loadScourCriticalBridgeIndicators",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const c of response.data ?? []) {
          this.scourCriticalBridgeIndicators.push(c);
        }
      });
    },
    loadDepartmentStructureTypeDescriptions(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.DEPARTMENT_STRUCTURE_TYPE_DESCRIPTIONS,
        auth: user,
        method: "loadDepartmentStructureTypeDescriptions",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const c of response.data ?? []) {
          this.departmentStructureTypeDescriptions.push(c);
        }
      });
    },
    loadElementDefinitions(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.ELEMENT_DEFINITIONS,
        auth: user,
        method: "loadElementDefinitions",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const elementDef of response.data ?? []) {
          this.elementDefinitions.push(elementDef);
        }
      });
    },
    loadElementChildren(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.ELEMENT_CHILDREN,
        auth: user,
        method: "loadElementChildren",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const elemKey of Object.keys(response.data) ?? []) {
          this.elementChildren[elemKey] = response.data[elemKey];
        }
      });
    },
    loadFlexActions(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.FLEX_ACTIONS,
        auth: user,
        method: "loadFlexActions",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const flexAction of response.data ?? []) {
          this.flexActions.push({
            flexAction,
          });
        }
      });
    },
    loadBridgeActions(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.BRIDGE_ACTIONS,
        auth: user,
        method: "loadBridgeActions",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const bridgeAction of response.data ?? []) {
          this.bridgeActions.push({
            bridgeAction,
          });
        }
      });
    },
    loadOrganizations(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.ORGANIZATIONS,
        auth: user,
        method: "loadOrganizations",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const organization of response.data ?? []) {
          this.organizations.push({
            organization,
          });
        }
      });
    },
    loadSPNOrganizations(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.SPNORGANIZATIONS,
        auth: user,
        method: "loadSPNOrganizations",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const spnOrganization of response.data ?? []) {
          this.spnOrganizations.push({
            spnOrganization,
          });
        }
      });
    },

    loadElementStateDefinitions(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.ELEMENT_STATE_DEFINITIONS,
        auth: user,
        method: "loadElementStateDefinitions",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const elemKey of Object.keys(response.data) ?? []) {
          this.elementStateDefinitions[elemKey] = response.data[elemKey];
        }
      });
    },
    loadDocTypes(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.DOC_TYPES,
        auth: user,
        method: "loadDocTypes",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        for (const c of response.data ?? []) {
          this.docTypes.push(c);
        }
      });
    },
    loadInspectionReportSection(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.INSPECTION_REPORT_SECTION_TYPES,
        auth: user,
        method: "loadInspectionReportSection",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.inspectionReportSection = response.data;
      });
    },
    loadInspectorId(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.INSPECTOR_IDS,
        auth: user,
        method: "loadinspectorId",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.inspectorIds = response.data;
      });
    },
    loadCommentsFilters(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.COMMENTS_FILTERS,
        auth: user,
        method: "loadCommentsFilters",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.commentsFilters = response.data;
      });
    },
    loadServiceTypes(user) {
      const data = {
        endPoint: CONFIG_ENDPOINT.SERVICE_TYPES,
        auth: user,
        method: "loadServiceTypes",
      };
      return this.apiWorkerManager.postMessage(data).then((response) => {
        this.serviceTypes = response.data;
      });
    },
  },
});
function isPorX(inspectionsUnderwater) {
  return inspectionsUnderwater?.some(({ invFoundType }) => ["P", "X"].includes(invFoundType));
}
function isServiceTypeUnder01234(serviceTypeUnder) {
  return [0, 1, 2, 3, 4].includes(serviceTypeUnder);
}

function removeCodeInLongDescription(state, removeCodeInDesc, referenceName) {
  let values = [];
  for (const r of state.references.get(referenceName) ?? []) {
    const value = r.title.long ? `${r.value} - ${r.title.long}` : `${r.value}`;
    values.push({
      value: r.value,
      title: removeCodeInDesc ? r.title.long : value,
    });
  }
  return values;
}

function removeCodeInShortDescription(state, removeCodeInDesc, referenceName) {
  let values = [];
  for (const r of state.references.get(referenceName) ?? []) {
    const value = r.title.long ? `${r.value} - ${r.title.short}` : `${r.value}`;
    values.push({
      value: r.value,
      title: removeCodeInDesc ? r.title.short : value,
    });
  }
  return values;
}

function removeCodeInDescription(state, removeCodeInDesc, referenceName, showLongDesc) {
  let values = [];
  if (showLongDesc) {
    values = removeCodeInLongDescription(state, removeCodeInDesc, referenceName);
  } else {
    values = removeCodeInShortDescription(state, removeCodeInDesc, referenceName);
  }
  return values;
}

function sortByDescription(sortOrder, values, valueType) {
  if (sortOrder == SORT_ORDER.DESC) {
    values?.sort((a, b) => {
      if (valueType == TYPES.NUMBER) {
        return b.value - a.value;
      } else {
        return b.value.localeCompare(a.value);
      }
    });
  } else {
    values?.sort((a, b) => {
      if (valueType == TYPES.NUMBER) {
        return a.value - b.value;
      } else {
        return a.value.localeCompare(b.value);
      }
    });
  }
  return values;
}
